import { groupElementsByParagraph } from './transcriptGrouping';

import type { TranscriptElement } from '@src/types.ts/transcript';

/**
 * Extract plain text from transcript elements, excluding removed/cut content.
 */
export function extractTranscriptionText(
  elements: TranscriptElement[],
): string {
  return elements
    .filter(el => el.value && el.state !== 'removed' && el.state !== 'cut')
    .map(el => el.value)
    .join('');
}

interface FormatTranscriptOptions {
  withSpeakers: boolean;
  withTimecodes: boolean;
  getSpeakerName: (label: string | undefined) => string;
}

/**
 * Format a time in seconds to HH:MM:SS format
 */
export function formatTimecode(timeInSeconds: number): string {
  if (timeInSeconds === undefined || isNaN(timeInSeconds)) {
    return '00:00:00';
  }

  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds % 3600) / 60);
  const seconds = Math.floor(timeInSeconds % 60);

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Format flat TranscriptElement[] with configurable speaker and timecode options.
 *
 * Uses the shared groupElementsByParagraph utility to match the visual layout
 * shown in the transcript UI.
 */
export function formatTranscriptElements(
  elements: TranscriptElement[] | undefined | null,
  options: FormatTranscriptOptions,
): string {
  if (!elements || elements.length === 0) return '';

  const { withSpeakers, withTimecodes, getSpeakerName } = options;

  // Filter out removed/cut elements before grouping
  const activeElements = elements.filter(
    el => !el.state || !['removed', 'cut'].includes(el.state),
  );

  if (activeElements.length === 0) return '';

  const groups = groupElementsByParagraph(activeElements);

  // Filter out groups with no visible text content
  const visibleGroups = groups.filter(group =>
    group.elements.some(
      ({ element: el }) => el.value && el.value.trim() !== '',
    ),
  );

  let result = '';
  let lastSpeaker: string | undefined | null = null;

  for (const group of visibleGroups) {
    const text = group.elements
      .map(({ element: el }) => el.value ?? '')
      .join('')
      .trim();

    if (!text) continue;

    if (
      withSpeakers &&
      (lastSpeaker === null || group.speakerLabel !== lastSpeaker)
    ) {
      result += `${getSpeakerName(group.speakerLabel)}\n`;
    }

    if (withTimecodes && group.startTime != null) {
      const timecode = formatTimecode(group.startTime);
      result += `[${timecode}] `;
    }

    result += `${text}\n\n`;
    lastSpeaker = group.speakerLabel;
  }

  return result;
}
